import { useEffect, useRef } from 'react';

interface ElementOuterClickProps {
  selector: string | string[];
  enabled?: boolean;
  callback: (e: MouseEvent) => void;
}

/**
 * Hook to handle outer click of an element
 * @param params.selector - element selector to check if clicked outside
 * @param params.callback - callback to call if clicked outside
 * @param params.enabled - enable or disable the hook
 * @description
 * This hook has sibling hook useClickOutside
 */
export const useElementOuterClick = ({ selector, enabled = true, callback }: ElementOuterClickProps) => {
  const callbackRef = useRef(callback);
  const selectorRef = useRef(selector);

  useEffect(() => {
    callbackRef.current = callback;
    selectorRef.current = selector;
  }, [callback, enabled, selector]);

  useEffect(() => {
    const onDocumentClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      if (Array.isArray(selectorRef.current)) {
        if (
          !selectorRef.current.some((selector) => document.querySelector(selector)?.contains(target)) &&
          !selectorRef.current.some((selector) => target.classList.contains(selector.replace('.', '')))
        ) {
          callbackRef.current(e);
        }
      } else {
        if (
          !document.querySelector(selectorRef.current)?.contains(target) &&
          !target.classList.contains(selectorRef.current.replace('.', ''))
        ) {
          callbackRef.current(e);
        }
      }
    };
    if (enabled) {
      document.addEventListener('click', onDocumentClick);
    } else {
      document.removeEventListener('click', onDocumentClick);
    }
    return () => {
      document.removeEventListener('click', onDocumentClick);
    };
  }, [enabled]);
};
